import {
  TextField,
  Box,
  Typography,
  Divider,
  Stack,
  lighten,
  darken,
  useTheme,
} from "@mui/material";
import { RegisterOptions, useFormContext } from "react-hook-form";
import React from "react";
import { formatUSPhoneNumber } from "@/global/utils/utils";
import { InputErrorMessage } from "./InputErrorMessage";

interface Props {
  fieldName?: string;
  defaultValue?: string;
  options?: Omit<RegisterOptions, "minLength" | "maxLength"> & {
    minLength?: {
      message?: string;
    };
    maxLength?: {
      message?: string;
    };
  };
  placeholder?: string;
}

function insertChar(str: string, position: number, char: string) {
  return str.slice(0, position) + char + str.slice(position);
}

const formatPhoneNumber = (str: string) => {
  let formatted = str;
  if (str.length == 3) formatted = str + "-";
  if (str.length == 7) formatted = str + "-";
  if (str.length == 4 && str[4] !== "-") formatted = insertChar(str, 3, "-");
  if (str.length == 8 && str[8] !== "-") formatted = insertChar(str, 7, "-");
  return formatted;
};

export function PhoneNumberInputV2({
  fieldName = "phoneNumber",
  defaultValue = "",
  options,
  placeholder,
}: Props) {
  const theme = useTheme();
  const {
    setValue,
    getValues,
    clearErrors,
    register,
    formState: { errors },
  } = useFormContext();

  const errorMessage = errors[fieldName]?.message as string;
  const [phoneNumber, setPhoneNumber] = React.useState(
    formatUSPhoneNumber(defaultValue)
  );
  const [warningMessage, setWarningMessage] = React.useState("");

  const handleChange = (ev: any) => {
    let value = ev.target.value;

    // fixing autofill issue
    if (
      ev.inputType === "insertReplacementText" ||
      ev.target.matches(":-webkit-autofill") ||
      (ev.inputType == undefined && ev.target.value.length >= 10)
    ) {
      const formattedNumber = formatUSPhoneNumber(value);
      setPhoneNumber(formattedNumber);
      return;
    }

    const isNumber = /[0-9]/.test(ev.nativeEvent.data);
    const isBackspace = ev.nativeEvent.inputType === "deleteContentBackward";
    if (isBackspace) setPhoneNumber(value);
    if (isNumber && value.length <= 12) {
      setPhoneNumber(formatPhoneNumber(value));
    }
    clearErrors(fieldName);
    if (!isNumber || !isBackspace) return;
  };

  // registering to hook form ctx
  React.useEffect(() => {
    register(fieldName, {
      required: options?.required ?? "Phone Number is required",
      minLength: {
        value: 10,
        message:
          options?.minLength?.message || "Phone Number should have 10 digits",
      },
      maxLength: {
        value: 10,
        message:
          options?.maxLength?.message || "Phone Number should have 10 digits",
      },
    });
  }, [register]);

  // setting value to hook form ctx and showing warning message
  React.useEffect(() => {
    const value = phoneNumber.replaceAll("-", "");
    setValue(fieldName, value);
    let timeout = setTimeout(() => {
      if ((value?.length < 10 && value?.length > 0) || value?.length > 10) {
        setWarningMessage(
          options?.maxLength?.message || "Phone Number should have 10 digits"
        );
      } else {
        setWarningMessage("");
      }
      clearTimeout(timeout);
    }, 300);
  }, [phoneNumber]);

  return (
    <Box>
      {/* <input
        name="phoneNumber"
        onChange={(ev) => {
          console.log(ev.target.value);
        }}
      ></input> */}

      <TextField
        name={fieldName}
        size="small"
        fullWidth
        placeholder={placeholder || "Phone Number"}
        // onChange={handleChange}
        value={phoneNumber}
        InputProps={{
          onChange: handleChange,
          startAdornment: (
            <Stack direction={"row"}>
              <Box>
                <Typography color={theme.palette.divider}>+1</Typography>
              </Box>
              <Box px={1}>
                <Divider orientation="vertical" />
              </Box>
            </Stack>
          ),
        }}
      />

      <Box
        p={warningMessage ? 1 : 0}
        mt={warningMessage ? 1 : 0}
        bgcolor={lighten(theme.palette.warning.main, 0.9)}
        borderRadius={"4px"}
        width={"100%"}
        height={warningMessage ? "24px" : "0px"}
        sx={{
          transition: "all 0.2s ease-in-out",
          opacity: warningMessage ? 1 : 0,
        }}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"left"}
      >
        <Typography
          variant="body2"
          color={darken(theme.palette.warning.main, 0.4)}
        >
          {warningMessage}
        </Typography>
      </Box>

      <Box>
        <InputErrorMessage message={errorMessage} />
      </Box>
    </Box>
  );
}
